import { useState, useCallback, lazy, Suspense } from "react";
import { T } from "../tokens";
import { PitchCanvas, StatsPanel, PlayerSidebar, Timeline } from "../App";
import TopBar from "./TopBar";
import UploadPanel from "./UploadPanel";
import LeftPanel from "./LeftPanel";
import InspectorPanel from "./InspectorPanel";
import { CameraModeRow, AnnotationToolbar, CAMERA_MODES } from "./CameraToolbar";
import AnnotationLayer from "./AnnotationLayer";
import ScenarioBuilder from "./ScenarioBuilder";
import ScenarioBranches from "./ScenarioBranches";
import { estimateOutcome } from "./scenarioEngine";

const PitchCanvas3D = lazy(() => import("../PitchCanvas3D"));

const SCENARIO_PALETTE = ["#1BAF7A", "#8B5CF6", "#F59E0B", "#EF4444", "#00D4FF", "#EC4899"];

export default function Workbench(props) {
  const {
    allRows, frameMap, frames, poseMap, currentIdx, setCurrentIdx,
    playing, setPlaying, speed, setSpeed,
    showHeatmap, setShowHeatmap, showTrails, setShowTrails, showPose, setShowPose,
    selectedPlayer, setSelectedPlayer, usingDemo, view3D, setView3D,
    selectedMode, setSelectedMode,
    uploadFile, setUploadFile, uploadStatus, jobId, jobProgress, showUploadPanel, setShowUploadPanel,
    handleVideoUpload, handleTrackingFile, handlePoseFile,
    playerStats, heatmaps, events, poseLoaded,
  } = props;

  // ── Workbench-only state (not shared with the plain dashboard) ──
  const [activeView, setActiveView]   = useState("actual");        // "actual" | scenario.id
  const [scenarios, setScenarios]     = useState([]);
  const [cameraMode, setCameraMode]   = useState("tactical");
  const [annotationTool, setAnnotationTool] = useState(null);
  const [annotationsByView, setAnnotationsByView] = useState({});  // { [view]: [...] }
  const [notes, setNotes]             = useState({});              // { [view]: text }
  const [outcomes, setOutcomes]       = useState({});              // { [scenarioId]: outcome }
  const [simulating, setSimulating]   = useState(false);

  const mode = CAMERA_MODES.find(m=>m.key===cameraMode) || CAMERA_MODES[1];

  const handleCameraMode = useCallback((key)=>{
    setCameraMode(key);
    const m = CAMERA_MODES.find(x=>x.key===key);
    if (m) setView3D(m.view3D);
  }, [setView3D]);

  const addScenario = useCallback(()=>{
    setScenarios(prev=>{
      const n = prev.length;
      const id = `scenario_${Date.now()}`;
      const label = `Scenario ${String.fromCharCode(65+n)}`; // A, B, C...
      const color = SCENARIO_PALETTE[n % SCENARIO_PALETTE.length];
      const frame = frames[currentIdx] ?? 0;
      return [...prev, { id, label, color, frame, instructions: {}, ballTarget: null }];
    });
  }, [frames, currentIdx]);

  const activeScenario = scenarios.find(s=>s.id===activeView) || null;

  const updateInstruction = useCallback((trackerId, text)=>{
    setScenarios(prev=>prev.map(s=>s.id!==activeView ? s : {
      ...s, instructions: { ...s.instructions, [trackerId]: text }
    }));
  }, [activeView]);

  const setBallTarget = useCallback((target)=>{
    setScenarios(prev=>prev.map(s=>s.id!==activeView ? s : { ...s, ballTarget: target }));
  }, [activeView]);

  const runSimulation = useCallback(()=>{
    if (!activeScenario) return;
    setSimulating(true);
    setTimeout(()=>{
      const rowsAtBranch = frameMap?.get(activeScenario.frame) || [];
      const outcome = estimateOutcome(activeScenario, rowsAtBranch);
      setOutcomes(prev=>({ ...prev, [activeScenario.id]: outcome }));
      setSimulating(false);
    }, 550);
  }, [activeScenario, frameMap]);

  const annotations = annotationsByView[activeView] || [];
  const addAnnotation = useCallback((ann)=>{
    setAnnotationsByView(prev=>({ ...prev, [activeView]: [...(prev[activeView]||[]), ann] }));
  }, [activeView]);
  const clearAnnotations = useCallback(()=>{
    setAnnotationsByView(prev=>({ ...prev, [activeView]: [] }));
  }, [activeView]);

  const annotationColor = activeScenario ? activeScenario.color : "#FFD400";

  return (
    <div style={{background:T.bg,minHeight:"100vh",color:T.textPri,
      fontFamily:"'Inter','Segoe UI',system-ui,sans-serif",display:"flex",flexDirection:"column"}}>

      <TopBar
        frames={frames} currentIdx={currentIdx}
        activeView={activeView} setActiveView={setActiveView} scenarios={scenarios}
        onRunSimulation={runSimulation} simulating={simulating}
        onToggleUpload={()=>setShowUploadPanel(v=>!v)} showUploadPanel={showUploadPanel}
        selectedMode={selectedMode} usingDemo={usingDemo} allRows={allRows}
        poseLoaded={poseLoaded} poseMap={poseMap}
        showHeatmap={showHeatmap} setShowHeatmap={setShowHeatmap}
        showTrails={showTrails} setShowTrails={setShowTrails}
        showPose={showPose} setShowPose={setShowPose}
        handleTrackingFile={handleTrackingFile} handlePoseFile={handlePoseFile}
      />

      {showUploadPanel && (
        <UploadPanel
          selectedMode={selectedMode} setSelectedMode={setSelectedMode}
          uploadFile={uploadFile} setUploadFile={setUploadFile}
          uploadStatus={uploadStatus} jobId={jobId} jobProgress={jobProgress}
          handleVideoUpload={handleVideoUpload}
        />
      )}

      <div style={{flex:1,display:"grid",gridTemplateColumns:"240px 1fr 300px",overflow:"hidden"}}>

        {/* Left panel */}
        <div style={{borderRight:`1px solid ${T.border}`,background:T.surface,overflowY:"auto"}}>
          <LeftPanel
            frames={frames} currentIdx={currentIdx} setCurrentIdx={setCurrentIdx}
            events={events} scenarios={scenarios}
            activeView={activeView} setActiveView={setActiveView}
            onAddScenario={addScenario}
          />
        </div>

        {/* Center: camera row, viewer, annotations, scenario builder, timeline */}
        <div style={{display:"flex",flexDirection:"column",minWidth:0}}>
          <CameraModeRow activeMode={cameraMode} setActiveMode={handleCameraMode} selectedPlayer={selectedPlayer}/>

          <div style={{flex:1,background:T.bg,position:"relative",minHeight:300}}>
            {view3D ? (
              <Suspense fallback={
                <div style={{display:"flex",alignItems:"center",justifyContent:"center",
                  height:"100%",color:T.textDim,fontSize:12}}>
                  Loading 3D scene...
                </div>
              }>
                <PitchCanvas3D
                  frameMap={frameMap} frames={frames} currentIdx={currentIdx}
                  showTrails={showTrails}
                  selectedPlayer={selectedPlayer}
                  onSelectPlayer={(id)=>setSelectedPlayer(p=>p===id?null:id)}
                  poseMap={poseMap}
                  showSkeleton={showPose}
                  externalPreset={mode.preset || undefined}
                  externalFollow={mode.follow}
                  hideBuiltInCameraBar
                />
              </Suspense>
            ) : (
              <PitchCanvas
                frameMap={frameMap} frames={frames} currentIdx={currentIdx}
                playerStats={playerStats} allRows={allRows}
                showHeatmap={showHeatmap} showTrails={showTrails} showPose={showPose}
                heatmaps={heatmaps} poseMap={poseMap}
                selectedPlayer={selectedPlayer} onSelectPlayer={setSelectedPlayer}
              />
            )}

            <AnnotationLayer
              tool={annotationTool}
              annotations={annotations}
              onAdd={addAnnotation}
              color={annotationColor}
            />
            <AnnotationToolbar
              activeTool={annotationTool} setActiveTool={setAnnotationTool}
              onClear={clearAnnotations} annotationCount={annotations.length}
            />
          </div>

          {activeScenario && (
            <ScenarioBuilder
              scenario={activeScenario}
              frameRows={frameMap?.get(activeScenario.frame)}
              onUpdateInstruction={updateInstruction}
              onSetBallTarget={setBallTarget}
              outcome={outcomes[activeScenario.id]}
            />
          )}

          <div style={{borderTop:`1px solid ${T.border}`,background:T.surface}}>
            <Timeline
              frames={frames} currentIdx={currentIdx} setCurrentIdx={setCurrentIdx}
              playing={playing} setPlaying={setPlaying}
              speed={speed} setSpeed={setSpeed} events={events}
            />
            <ScenarioBranches
              frames={frames} scenarios={scenarios}
              activeView={activeView} setActiveView={setActiveView}
              setCurrentIdx={setCurrentIdx}
            />
          </div>
        </div>

        {/* Right: inspector */}
        <div style={{borderLeft:`1px solid ${T.border}`,overflowY:"auto"}}>
          <InspectorPanel
            selectedPlayer={selectedPlayer} playerStats={playerStats}
            frameMap={frameMap} frames={frames} currentIdx={currentIdx}
            events={events} notes={notes} setNotes={setNotes}
            activeView={activeView}
          />
        </div>
      </div>

      {/* Bottom: stats + player list strip (kept from the original dashboard) */}
      <div style={{borderTop:`1px solid ${T.border}`,background:T.surface,
        display:"flex",gap:0,maxHeight:190,overflow:"hidden"}}>
        <div style={{flex:1,padding:"10px 14px",overflowY:"auto",borderRight:`1px solid ${T.border}`}}>
          <StatsPanel allRows={allRows} frames={frames} currentIdx={currentIdx} playerStats={playerStats}/>
        </div>
        <div style={{flex:1,padding:"10px 14px",overflowY:"auto"}}>
          <PlayerSidebar
            playerStats={playerStats} selectedPlayer={selectedPlayer}
            onSelectPlayer={setSelectedPlayer}
            frameMap={frameMap} frames={frames} currentIdx={currentIdx}
            poseMap={poseMap}
          />
        </div>
      </div>
    </div>
  );
}
